import * as React from 'react'
import Highlighter from 'react-highlight-words'
import ReactPaginate from 'react-paginate'
import { Action, none, Option, some } from 'widgets-for-react'
import { LoaderCircle } from '../../../accommodations/models/loading'
import { prefixUrlWithLocale } from '../../../utils/url'
import { PageLoader } from '../../shared/loaders/PageLoader'
import {
  ContentAuditSuspect,
  ContentAuditSuspectUser,
  DecisionState,
  filterableTypes,
  LayoutProps,
  NoViolationState,
  PagedContentAuditSuspects,
  State,
  SuspectsFilter,
  suspectStatusses,
  ViolationState
} from './contentAuditSuspectsState'

const ViolationForm = (props: {
  violation: ViolationState
  onToggleStrike: (_: boolean) => void
  user: ContentAuditSuspectUser
  disabled: boolean
  submit: () => void
}) => (
  <div className="box box--warning">
    <div className="box__body">
      <table className="table--bordered violation-form">
        <tr>
          <td>{I18n.t('superadmin.content_audit_suspects.user.id')}</td>
          <td>
            <a
              href={prefixUrlWithLocale(`/superadmin/users/${props.user.id}`)}
              target="_blank"
            >
              {props.user.id}
            </a>
          </td>
        </tr>
        <tr>
          <td>{I18n.t('superadmin.content_audit_suspects.user.full_name')}</td>
          <td>{props.user.full_name}</td>
        </tr>
        <tr>
          <td>
            {I18n.t('superadmin.content_audit_suspects.user.violation_strikes')}
          </td>
          <td>{props.user.violation_strikes ?? 0}</td>
        </tr>
      </table>
      <div className="form-row">
        <div className="form-cell">
          <div className="input boolean">
            <label className="boolean control-label checkbox">
              <input
                className="boolean"
                type="checkbox"
                checked={props.violation.strike}
                disabled={props.disabled}
                onChange={v => {
                  props.onToggleStrike(v.currentTarget.checked)
                }}
              />
              {I18n.t(
                'superadmin.content_audit_suspects.violation.labels.strike'
              )}
            </label>
          </div>
        </div>
      </div>
      <div className="form-row">
        <div className="form-cell">
          <button
            name="button"
            type="submit"
            className="btn btn--primary"
            onClick={() => {
              props.submit()
            }}
            disabled={props.disabled}
          >
            {I18n.t(
              'superadmin.content_audit_suspects.violation.buttons.mark_as_violation'
            )}
          </button>
        </div>
      </div>
    </div>
  </div>
)

const NoViolationForm = (props: {
  noViolation: NoViolationState
  disabled: boolean
  submit: () => void
}) => (
  <div className="box box--purple-100">
    <div className="box__body">
      <div className="form-row">
        <div className="form-cell">
          <button
            name="button"
            type="submit"
            className="btn btn--ghost-purple"
            onClick={() => {
              props.submit()
            }}
            disabled={props.disabled}
          >
            {I18n.t(
              'superadmin.content_audit_suspects.violation.buttons.mark_as_no_violation'
            )}
          </button>
        </div>
      </div>
    </div>
  </div>
)

const ContentAuditSuspectsOverview = (props: {
  pagedSuspects: PagedContentAuditSuspects
  onToggleViolationForm: (_: ContentAuditSuspect) => void
  onToggleNoViolationForm: (_: ContentAuditSuspect) => void
  buttonsDisabled: boolean
  decision: Option<DecisionState>
  onToggleStrike: (_: boolean) => void
  onViolationSubmit: () => void
  onNoViolationSubmit: () => void
}) => (
  <table className="table--bordered index table--zebra">
    <thead>
      <tr>
        <th>
          {I18n.t('superadmin.content_audit_suspects.columns.resource_type')}
        </th>
        <th>{I18n.t('superadmin.content_audit_suspects.columns.user_id')}</th>
        <th>{I18n.t('superadmin.content_audit_suspects.columns.status')}</th>
        <th>{I18n.t('superadmin.content_audit_suspects.columns.source')}</th>
        <th>{I18n.t('superadmin.content_audit_suspects.columns.match')}</th>
        <th />
      </tr>
    </thead>
    <tbody>
      {props.pagedSuspects.total_items > 0 ? (
        props.pagedSuspects.suspects.map(suspect => (
          <tr key={suspect.id}>
            <td>
              {I18n.t(
                `superadmin.content_audit_suspects.resource_types.${suspect.resource_type}`
              )}
            </td>
            <td>
              <a
                href={prefixUrlWithLocale(
                  `/superadmin/users/${suspect.user_id}`
                )}
                target="_blank"
              >
                {suspect.user_id}
              </a>
            </td>
            <td>
              {I18n.t(
                `superadmin.content_audit_suspects.statusses.${suspect.status}`
              )}
            </td>
            <td className="cell-66">
              <Highlighter
                textToHighlight={suspect.source}
                searchWords={[suspect.match]}
                caseSensitive={false}
                autoEscape={true}
              />
              <p>
                <a
                  href={prefixUrlWithLocale(
                    `/superadmin/content_audit_suspects/${suspect.id}/redirect`
                  )}
                  className="btn btn--white"
                  target="_blank"
                >
                  {I18n.t('superadmin.content_audit_suspects.buttons.edit')}
                </a>
              </p>
              {props.decision.v.kind === 'r' &&
              props.decision.v.v.value.suspect.id === suspect.id ? (
                props.decision.v.v.kind === 'violation' ? (
                  props.decision.v.v.value.user.kind === 'loaded' ? (
                    <ViolationForm
                      violation={props.decision.v.v.value}
                      onToggleStrike={props.onToggleStrike}
                      disabled={props.buttonsDisabled}
                      submit={props.onViolationSubmit}
                      user={props.decision.v.v.value.user.value}
                    />
                  ) : (
                    <div className="box box--warning">
                      <div className="box__body">
                        <LoaderCircle />
                      </div>
                    </div>
                  )
                ) : (
                  <NoViolationForm
                    noViolation={props.decision.v.v.value}
                    disabled={props.buttonsDisabled}
                    submit={props.onNoViolationSubmit}
                  />
                )
              ) : (
                <></>
              )}
            </td>
            <td>{suspect.match}</td>
            <td>
              {suspect.status !== 'no_violation' ? (
                <p>
                  <button
                    className="btn"
                    disabled={props.buttonsDisabled}
                    onClick={e => {
                      e.preventDefault()
                      props.onToggleNoViolationForm(suspect)
                    }}
                  >
                    {I18n.t(
                      'superadmin.content_audit_suspects.buttons.no_violation'
                    )}
                  </button>
                </p>
              ) : (
                <></>
              )}
              {suspect.status !== 'violation' ? (
                <p>
                  <button
                    className="btn btn--red"
                    disabled={props.buttonsDisabled}
                    onClick={e => {
                      e.preventDefault()
                      props.onToggleViolationForm(suspect)
                    }}
                  >
                    {I18n.t(
                      'superadmin.content_audit_suspects.buttons.violation'
                    )}
                  </button>
                </p>
              ) : (
                <></>
              )}
            </td>
          </tr>
        ))
      ) : (
        <tr>
          <td colSpan={5}>
            {I18n.t('superadmin.content_audit_suspects.no_suspects_found')}
          </td>
        </tr>
      )}
    </tbody>
  </table>
)

function FilterOption<T extends string>(props: {
  label: string
  options: T[]
  current: Option<T>
  onChange: (_: Option<T>) => void
  disabled: boolean
}) {
  return (
    <div className="input">
      <label className="string control-label">{props.label}</label>
      <select
        onChange={e => {
          props.onChange(
            e.currentTarget.value ? some(e.currentTarget.value as T) : none()
          )
        }}
        disabled={props.disabled}
        value={props.current.v.kind === 'r' ? props.current.v.v : ''}
      >
        <option value="" />
        {props.options.map(x => (
          <option key={x} value={x}>
            {I18n.t(`superadmin.content_audit_suspects.filter.options.${x}`)}
          </option>
        ))}
      </select>
    </div>
  )
}

const Filter = (props: {
  filter: SuspectsFilter
  onChangeFilter: (_: Action<SuspectsFilter>) => void
  disabled: boolean
}) => (
  <div className="grid-50">
    <div className="form-row">
      <div className="form-cell">
        <FilterOption
          label={I18n.t(
            'superadmin.content_audit_suspects.filter.labels.status'
          )}
          options={suspectStatusses.map(x => x)}
          current={props.filter.status}
          disabled={props.disabled}
          onChange={v => {
            props.onChangeFilter(f => ({
              ...f,
              status: v
            }))
          }}
        />
      </div>
    </div>
    <div className="form-row">
      <div className="form-cell">
        <label className="string control-label">
          {I18n.t('superadmin.content_audit_suspects.filter.labels.type')}
        </label>
        {filterableTypes.map(x => (
          <div className="input boolean" key={x}>
            <label className="boolean control-label checkbox">
              <input
                className="boolean"
                type="checkbox"
                checked={props.filter.type[x]}
                disabled={props.disabled}
                onChange={v => {
                  props.onChangeFilter(f => {
                    const type = f.type
                    f.type[x] = v.currentTarget.checked
                    return {
                      ...f,
                      type
                    }
                  })
                }}
              />
              {I18n.t(`superadmin.content_audit_suspects.filter.options.${x}`)}
            </label>
          </div>
        ))}
      </div>
    </div>
    <div className="form-row">
      <div className="form-cell">
        <div className="input boolean">
          <label className="boolean control-label checkbox">
            <input
              className="boolean"
              type="checkbox"
              checked={props.filter.booking_adverts}
              disabled={props.disabled}
              onChange={v => {
                props.onChangeFilter(f => ({
                  ...f,
                  booking_adverts: v.currentTarget.checked
                }))
              }}
            />
            {I18n.t(
              'superadmin.content_audit_suspects.filter.labels.booking_adverts'
            )}
          </label>
        </div>
      </div>
    </div>
    <div className="form-row">
      <div className="form-cell">
        <div className="input boolean">
          <label className="boolean control-label checkbox">
            <input
              className="boolean"
              type="checkbox"
              checked={props.filter.published_adverts}
              disabled={props.disabled}
              onChange={v => {
                props.onChangeFilter(f => ({
                  ...f,
                  published_adverts: v.currentTarget.checked
                }))
              }}
            />
            {I18n.t(
              'superadmin.content_audit_suspects.filter.labels.published_adverts'
            )}
          </label>
        </div>
      </div>
    </div>
  </div>
)

const pageCount = (suspects: PagedContentAuditSuspects): number =>
  Math.ceil(suspects.total_items / suspects.per_page)

const buttonsDisabled = (state: State): boolean =>
  state.kind !== 'loaded' ||
  (state.decision.v.kind === 'r' &&
    ((state.decision.v.v.kind === 'violation' &&
      state.decision.v.v.value.markViolation.kind === 'loading') ||
      (state.decision.v.v.kind === 'no_violation' &&
        state.decision.v.v.value.markNoViolation.kind === 'loading')))

export const ContentAuditSuspectsLayout = (props: LayoutProps): JSX.Element => (
  <section className="page-section audit-suspects--overview">
    <div id="suspects-top" />
    <header className="grid-100 page-section__head">
      <h1 className="page-section__title">
        {I18n.t('superadmin.content_audit_suspects.title')}
      </h1>
    </header>
    <div className="page-section">
      <div className="page-section__body">
        <Filter
          filter={props.state.filter}
          onChangeFilter={props.onFilterChange}
          disabled={buttonsDisabled(props.state)}
        />
      </div>
    </div>

    <>
      {props.state.kind === 'loaded' ? (
        <>
          <div className="page-section">
            <div className="page-section__body">
              <div className="grid-100 table-wrapper">
                <ContentAuditSuspectsOverview
                  pagedSuspects={props.state.suspects}
                  onToggleViolationForm={props.onToggleMarkViolationForm}
                  onToggleNoViolationForm={props.onToggleNoViolationForm}
                  onViolationSubmit={props.onViolationSubmit}
                  onNoViolationSubmit={props.onNoViolationSubmit}
                  decision={props.state.decision}
                  buttonsDisabled={buttonsDisabled(props.state)}
                  onToggleStrike={props.onToggleStrike}
                />
                {pageCount(props.state.suspects) > 1 ? (
                  <div className="pagination--section">
                    <ReactPaginate
                      pageCount={pageCount(props.state.suspects)}
                      initialPage={props.state.suspects.page || 0}
                      pageRangeDisplayed={5}
                      marginPagesDisplayed={2}
                      containerClassName="pagination"
                      nextLabel=""
                      previousLabel=""
                      onPageChange={i => {
                        document
                          .getElementById('suspects-top')
                          .scrollIntoView(false)
                        props.onPageChange(i.selected)
                      }}
                    />
                  </div>
                ) : (
                  <></>
                )}
              </div>
            </div>
          </div>
        </>
      ) : props.state.suspects.kind === 'loading' ? (
        <PageLoader />
      ) : (
        <>Something went wrong, try again later</>
      )}
    </>
  </section>
)
